import {
  useGetOffersCargos,
  useMultipleGetOffersCargos,
} from "@/entities/get-offers-cargos/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import type { LoadableData } from "@/shared/lib/loadable";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { ShipperCargoListPageProps } from "../model";

export const useShipperCargoListPageProps = (): ShipperCargoListPageProps => {
  // Auth
  const auth = useAuth();

  // State
  const [getDraftOffersCargosParams, setGetDraftOffersCargosParams] =
    useState<
      Parameters<
        ShipperCargoListPageProps["onGetDraftOffersCargosParamsChange"]
      >[0]
    >();
  const [offerCargoPagination, setOfferCargoPagination] = useState<{
    page: number;
    perPage: number;
  }>({
    page: 1,
    perPage: 50,
  });

  // Local Storage
  // 荷物作成画面に遷移する際に、記入途中の情報をLocalStorageから削除する
  const [_, __, removeInProgressValues] =
    useInProgressValuesLocalCache<unknown>("ShipperCargoCreatePage");

  // Backend API
  const getDraftOffersCargos = useGetOffersCargos(
    {
      ...getDraftOffersCargosParams,
      companyId: auth.session.isAuthenticated
        ? auth.session.user.companyId
        : -1,
      stage: ["draft"],
      pagination: {
        // pageNumber: offerCargoPagination.page,
        // displayLimit: offerCargoPagination.perPage,
        // WORKAROUND: FREIGHT_MATCHING-320 【荷主/車主】新規荷物を登録した際、新しく登録した荷物が2ページ目の頭に表示されてしまう
        // WORKAROUND: こちらのバグがデモに支障あるため、一旦workaroundを入れています。正しくはAPIのI/Fにソート条件を含めるべきです。
        displayLimit: 10000,
      },
    },
    {
      enabled: auth.session.isAuthenticated,
    },
  );

  const getOffersCargosPerProcess = useMultipleGetOffersCargos(
    Object.fromEntries(
      (getDraftOffersCargos.data?.data.cargos ?? []).map((cargo) => [
        cargo.process.id,
        {
          processId: cargo.process.id,
        },
      ]),
    ),
    {
      enabled: getDraftOffersCargos.status === "success",
      sortFunc: (a, b) => {
        if (!a.processId || !b.processId) {
          return 0;
        }
        // 降順でソート
        return b.processId - a.processId;
      },
    },
  );

  // Actions
  const navigate = useNavigate();
  const onCreateOfferCargoClick: ShipperCargoListPageProps["onCreateOfferCargoClick"] =
    () => {
      removeInProgressValues();
      navigate({ to: "/shipper/cargos/create" });
    };
  const onGetDraftOffersCargosParamsChange: ShipperCargoListPageProps["onGetDraftOffersCargosParamsChange"] =
    (filter) => {
      setGetDraftOffersCargosParams(filter);
    };
  const onGetDraftOffersCargosParamsReset: ShipperCargoListPageProps["onGetDraftOffersCargosParamsReset"] =
    () => {
      setGetDraftOffersCargosParams(undefined);
    };

  // Props
  const draftOfferCargos = useMemo<
    ShipperCargoListPageProps["draftOfferCargos"]
  >(() => {
    if (getDraftOffersCargos.status === "pending") {
      return {
        status: "loading",
        data: undefined,
      };
    }
    if (getDraftOffersCargos.status === "error") {
      return {
        status: "error",
        error: { message: JSON.stringify(getDraftOffersCargos.error) },
      };
    }
    const data = getDraftOffersCargos.data.data.cargos?.map((cargo) => {
      const process: LoadableData<
        ShipperCargoListPageProps["draftOfferCargos"]
      >["data"][0]["process"] = (() => {
        const offerCargosInProcess =
          getOffersCargosPerProcess[cargo.process.id];
        if (
          !offerCargosInProcess ||
          offerCargosInProcess.status === "pending"
        ) {
          return {
            status: "loading",
            data: undefined,
          };
        }
        if (offerCargosInProcess.status === "error") {
          return {
            status: "error",
            error: { message: JSON.stringify(offerCargosInProcess.error) },
          };
        }
        const deliveryProposalOfferCargos =
          offerCargosInProcess.data.data.cargos?.filter(
            (offerCargo) => offerCargo.stage === "delivery_proposal",
          ) ?? [];
        const contractProposalOfferCargo =
          offerCargosInProcess.data.data.cargos?.find(
            (offerCargo) => offerCargo.stage === "contract_proposal",
          ) ?? undefined;
        const contractConcludedOfferCargo =
          offerCargosInProcess.data.data.cargos?.find(
            (offerCargo) => offerCargo.stage === "contract_concluded",
          ) ?? undefined;
        return {
          status: "success",
          data: {
            ...cargo.process,
            deliveryProposalOfferCargos,
            contractProposalOfferCargo,
            contractConcludedOfferCargo,
          },
        };
      })();
      return {
        ...cargo,
        process,
      };
    });
    return {
      status: "success",
      data: {
        data: data ?? [],
        pagination: {
          ...offerCargoPagination,
          totalCount: getDraftOffersCargos.data.data.totalCount ?? 0,
          onPageChange: (page: number) =>
            setOfferCargoPagination((prev) => ({ ...prev, page })),
          onPerPageChange: (perPage: number) =>
            setOfferCargoPagination((prev) => ({ ...prev, perPage })),
        },
      },
    };
  }, [
    offerCargoPagination,
    getDraftOffersCargos.status,
    getDraftOffersCargos.error,
    getDraftOffersCargos.data,
    getOffersCargosPerProcess,
  ]);
  return {
    draftOfferCargos,
    getDraftOffersCargosParams,
    onCreateOfferCargoClick,
    onGetDraftOffersCargosParamsChange,
    onGetDraftOffersCargosParamsReset,
  };
};
